/**
 * Cross-tab bookkeeping for silent Axon SSO token refreshes.
 *
 * The timestamp of the last successful refresh is kept in localStorage so that all tabs and
 * windows share it. This lets a caller skip a redundant refresh when another caller (in this
 * or another tab) has already rotated the token, which matters because the IDP treats
 * concurrent refreshes as replay and ends the session.
 */

import { getCookie } from "utils/cookies";

const LAST_REFRESH_AT_KEY = "gl_last_token_refresh_at";
const AUTH_EXPIRES_AT_COOKIE_NAME = "Groundlight-Auth-Expires-At";
const PREEMPTIVE_REFRESH_BUFFER_MS = 30 * 1000;

/**
 * Returns when the last successful refresh completed, shared across tabs.
 */
export const getLastRefreshAt = (): number => Number(localStorage.getItem(LAST_REFRESH_AT_KEY)) || 0;

/**
 * Records when the last successful refresh completed, shared across tabs.
 */
export const setLastRefreshAt = (timestamp: number): void =>
  localStorage.setItem(LAST_REFRESH_AT_KEY, String(timestamp));

/**
 * Clears the refresh timestamp so that any pending or future 401 handler cannot
 * incorrectly skip a real refresh call by treating a pre-logout refresh as valid.
 * Call this whenever the session is being torn down.
 */
export const clearLastRefreshAt = (): void => localStorage.removeItem(LAST_REFRESH_AT_KEY);

/**
 * Returns when the current access token should be treated as expired.
 */
export const getAccessExpireAt = (): number => {
  const authExpiresAt = Number(getCookie(AUTH_EXPIRES_AT_COOKIE_NAME)) || 0;
  if (!authExpiresAt) {
    return 0;
  }
  return Math.max(authExpiresAt - PREEMPTIVE_REFRESH_BUFFER_MS, 0);
};

/**
 * Returns whether a request should refresh before using the current access token.
 */
export const shouldRefreshAccessToken = (now = Date.now()): boolean => {
  const accessExpireAt = getAccessExpireAt();
  return Boolean(accessExpireAt && now > accessExpireAt);
};
